import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { AttachIcon, CloseSmallIcon, SendIcon } from '#/shared/ui/icons'
import { VoiceInputButton } from '#/shared/ui/voice-recording-island'

interface FilePreview {
  id: string
  name: string
  url?: string
  kind: 'image' | 'other'
  ext: string
}

interface FileEditorProps {
  files?: FilePreview[]
  onFilesChange?: (files: FilePreview[]) => void
  onAddFiles?: (files: FileList) => void
  onCancel?: () => void
  onSave?: (files: FilePreview[], name: string) => void
  placeholder?: string
  accept?: string
  multiple?: boolean
  className?: string
  enableCancel?: boolean
  isSubmitting?: boolean
  saveLabel?: string
  compact?: boolean
  /** Extra content rendered between the input and the footer (compact view). */
  extraContent?: React.ReactNode
}

function toPreview(file: File): FilePreview {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  const isImage = file.type.startsWith('image/')
  return {
    id: `${Date.now()}-${file.name}`,
    name: file.name,
    url: URL.createObjectURL(file),
    kind: isImage ? 'image' : 'other',
    ext,
  }
}

function FileThumb({
  file,
  onRemove,
}: {
  file: FilePreview
  onRemove?: (id: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="relative h-25 w-[77px] shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-50">
      {file.kind === 'image' && file.url ? (
        <img
          src={file.url}
          alt={file.name}
          className="size-full object-cover"
        />
      ) : (
        <div className="flex size-full items-center justify-center text-xs text-gray-400">
          {file.name}
        </div>
      )}
      <span className="absolute bottom-1.5 left-1.5 flex items-center justify-center rounded border border-gray-200 bg-white px-1 py-0.5 text-xs uppercase text-gray-400">
        {file.ext || 'file'}
      </span>
      {onRemove && (
        <button
          type="button"
          aria-label={t('editor.file.remove', { name: file.name })}
          onClick={() => onRemove(file.id)}
          className="absolute top-1 right-1 flex size-4 cursor-pointer items-center justify-center rounded-full bg-white/90 text-gray-500 shadow hover:text-gray-900"
        >
          <CloseSmallIcon className="size-2" />
        </button>
      )}
    </div>
  )
}

function useFilesState(
  controlled: FilePreview[] | undefined,
  onChange: FileEditorProps['onFilesChange'],
) {
  const [internal, setInternal] = React.useState<FilePreview[]>([])
  const current = controlled ?? internal
  const setFiles = (next: FilePreview[]) => {
    if (controlled === undefined) setInternal(next)
    onChange?.(next)
  }
  const add = (list: FileList) => {
    const next = [...current, ...Array.from(list).map(toPreview)]
    setFiles(next)
  }
  const remove = (id: string) => setFiles(current.filter((f) => f.id !== id))
  return { current, add, remove }
}

function FileInputRow({
  placeholder,
  draft,
  setDraft,
  onPickClick,
  onPaste,
}: {
  placeholder: string
  draft: string
  setDraft: (v: string) => void
  onPickClick: () => void
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2.5">
      <button
        type="button"
        aria-label={t('editor.file.pick')}
        onClick={onPickClick}
        className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-gray-500 hover:text-gray-900"
      >
        <AttachIcon className="size-5" />
      </button>
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onPaste={onPaste}
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
      />
      {draft.length > 0 && (
        <button
          type="button"
          aria-label={t('editor.file.clear')}
          onClick={() => setDraft('')}
          className="flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
        >
          <CloseSmallIcon className="size-2" />
        </button>
      )}
      <VoiceInputButton value={draft} onChange={setDraft} />
    </div>
  )
}

function FileEditorActions({
  showCancel,
  isSubmitting,
  canSave,
  saveLabel,
  onCancel,
  onSave,
}: {
  showCancel: boolean
  isSubmitting: boolean
  canSave: boolean
  saveLabel: string
  onCancel?: () => void
  onSave: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2.5">
      {showCancel && (
        <Button
          variant="outline"
          size="xs"
          pressEffect
          disabled={isSubmitting}
          onClick={onCancel}
        >
          {t('editor.file.cancel')}
        </Button>
      )}
      <Button
        size="xs"
        pressEffect
        disabled={!canSave}
        style={{ minWidth: 82 }}
        onClick={onSave}
      >
        {isSubmitting ? (
          <span
            aria-hidden
            className="size-3 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        ) : (
          <SendIcon className="size-3" />
        )}
        {saveLabel}
      </Button>
    </div>
  )
}

function useDraftSync(fileCount: number, setDraft: (v: string) => void) {
  React.useEffect(() => {
    if (fileCount === 0) setDraft('')
  }, [fileCount, setDraft])
}

function useFileEditorState(
  files: FilePreview[] | undefined,
  onFilesChange: FileEditorProps['onFilesChange'],
  onAddFiles: FileEditorProps['onAddFiles'],
  isSubmitting: boolean,
) {
  const state = useFilesState(files, onFilesChange)
  const [draft, setDraft] = React.useState('')
  useDraftSync(state.current.length, setDraft)

  const handleFiles = (list: FileList) => {
    onAddFiles?.(list)
    state.add(list)
    const first = list.item(0)
    if (first) setDraft(first.name)
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (e.clipboardData.files.length > 0) {
      e.preventDefault()
      handleFiles(e.clipboardData.files)
    }
  }

  const hasFiles = state.current.length > 0
  const canSave = (hasFiles || draft.trim().length > 0) && !isSubmitting

  return { state, draft, setDraft, handleFiles, handlePaste, hasFiles, canSave }
}

function CompactFileFooter({
  isSubmitting,
  canSave,
  saveLabel,
  onCancel,
  onSave,
}: {
  isSubmitting: boolean
  canSave: boolean
  saveLabel: string
  onCancel?: () => void
  onSave: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="sticky bottom-0 z-10 flex h-14 items-center justify-end gap-2.5 border-t border-gray-200 bg-gray-100 px-4">
      {onCancel && (
        <Button
          variant="outline"
          size="xs"
          pressEffect
          disabled={isSubmitting}
          onClick={onCancel}
        >
          {t('editor.file.cancel')}
        </Button>
      )}
      <Button size="xs" pressEffect disabled={!canSave} onClick={onSave}>
        {isSubmitting && (
          <span
            aria-hidden
            className="size-3 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        )}
        {saveLabel}
      </Button>
    </div>
  )
}

function FileEditorCompactView({
  editorState,
  inputRef,
  accept,
  multiple,
  placeholder,
  isSubmitting,
  saveLabel,
  onCancel,
  onSave,
  extraContent,
}: {
  editorState: ReturnType<typeof useFileEditorState>
  inputRef: React.RefObject<HTMLInputElement | null>
  accept?: string
  multiple: boolean
  placeholder: string
  isSubmitting: boolean
  saveLabel: string
  onCancel?: () => void
  onSave?: (files: FilePreview[], name: string) => void
  extraContent?: React.ReactNode
}) {
  const { state, draft, setDraft, handlePaste, hasFiles, canSave } = editorState
  return (
    <div className="flex flex-col">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) editorState.handleFiles(e.target.files)
          e.target.value = ''
        }}
      />
      <div className="flex flex-col gap-4 p-4">
        {hasFiles && (
          <div className="flex flex-wrap items-end gap-2.5">
            {state.current.map((f) => (
              <FileThumb key={f.id} file={f} onRemove={state.remove} />
            ))}
          </div>
        )}
        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.75">
          <FileInputRow
            placeholder={placeholder}
            draft={draft}
            setDraft={setDraft}
            onPickClick={() => inputRef.current?.click()}
            onPaste={handlePaste}
          />
        </div>
      </div>
      {extraContent}
      <CompactFileFooter
        isSubmitting={isSubmitting}
        canSave={canSave}
        saveLabel={saveLabel}
        onCancel={onCancel}
        onSave={() => onSave?.(state.current, draft.trim())}
      />
    </div>
  )
}

function FileEditorDefaultView({
  props,
  editorState,
  inputRef,
}: {
  props: FileEditorProps
  editorState: ReturnType<typeof useFileEditorState>
  inputRef: React.RefObject<HTMLInputElement | null>
}) {
  const { t } = useTranslation()
  const {
    onCancel,
    onSave,
    placeholder,
    accept,
    multiple = true,
    className,
    enableCancel = true,
    isSubmitting = false,
    saveLabel,
  } = props
  const {
    state,
    draft,
    setDraft,
    handleFiles,
    handlePaste,
    hasFiles,
    canSave,
  } = editorState
  return (
    <div className={cn('flex w-full flex-col gap-2.5 p-2.5', className)}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) handleFiles(e.target.files)
          e.target.value = ''
        }}
      />
      {hasFiles && (
        <div className="flex flex-wrap items-end gap-2.5">
          {state.current.map((f) => (
            <FileThumb key={f.id} file={f} onRemove={state.remove} />
          ))}
        </div>
      )}
      <div className="flex h-8 items-center justify-between gap-2.5">
        <FileInputRow
          placeholder={placeholder ?? t('editor.file.placeholder')}
          draft={draft}
          setDraft={setDraft}
          onPickClick={() => inputRef.current?.click()}
          onPaste={handlePaste}
        />
        <FileEditorActions
          showCancel={Boolean(enableCancel && hasFiles && onCancel)}
          isSubmitting={isSubmitting}
          canSave={canSave}
          saveLabel={saveLabel ?? t('editor.file.save')}
          onCancel={onCancel}
          onSave={() => onSave?.(state.current, draft.trim())}
        />
      </div>
    </div>
  )
}

function FileEditor(props: FileEditorProps) {
  const { t } = useTranslation()
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const editorState = useFileEditorState(
    props.files,
    props.onFilesChange,
    props.onAddFiles,
    props.isSubmitting ?? false,
  )

  if (props.compact) {
    return (
      <FileEditorCompactView
        editorState={editorState}
        inputRef={inputRef}
        accept={props.accept}
        multiple={props.multiple ?? true}
        placeholder={props.placeholder ?? t('editor.file.placeholder')}
        isSubmitting={props.isSubmitting ?? false}
        saveLabel={props.saveLabel ?? t('editor.file.save')}
        onCancel={props.onCancel}
        onSave={props.onSave}
        extraContent={props.extraContent}
      />
    )
  }

  return (
    <FileEditorDefaultView
      props={props}
      editorState={editorState}
      inputRef={inputRef}
    />
  )
}

export { FileEditor, toPreview }
export type { FileEditorProps, FilePreview }
